<template>
  <div>
    <div v-if="isSelectorPanelVisible && !hasSearchOnly">
      <v-navigation-drawer
        :width="LeftSidePanelWidth"
        height="100vh"
        permanent
        :expand-on-hover="expandOnHover"
        mini-variant-width="45px"
        class="hyp-home-left-nav hyp-home-left-nav-light"
        :style="navDrawerStyle"
        @transitionend="handleUpdateMiniVariant"
      >
        <Logo
          :width="134"
          :height="134"
          :type="logoType"
          @click="toggleSelectorPanel"
          :disable-toggle="disableMenuToggle"
        ></Logo>

        <div class="hyp-left-side-panel-spacer-small"></div>

        <LeftSidePanelSection title="My Stuff" :is-mini="leftPanelMini">
          <CommsSelector
            v-if="hasMassCommAdminAccess"
            :="disabledComms"
          ></CommsSelector>

          <PlacesSelector :disabled="disabledPlaces"></PlacesSelector>

          <CollaborationSelector
            :disabled="disabledCollaboration"
          ></CollaborationSelector>

          <AlertsSelector :disabled="disabledAlerts"></AlertsSelector>

          <NotificationsSelector
            v-if="isNotificationsFeatureUnlocked"
            :disabled="disabledNotifications"
          ></NotificationsSelector>
        </LeftSidePanelSection>

        <LeftSidePanelSection title="Dashboards" :is-mini="leftPanelMini">
          <LatestSelector
            v-if="isLatestFeatureUnlocked"
            :disabled="disabledLatest"
          >
          </LatestSelector>
          <MonitorSelector :disabled="disabledWatch"></MonitorSelector>
          <SimpleSelector
            v-if="hasFiriAppAccess && isFiriAppLinkFeatureUnlocked"
            type="firi"
            title="Firi"
            url="https://influence.seerist.com/"
          ></SimpleSelector>
        </LeftSidePanelSection>

        <LeftSidePanelSection title="Analytics" :is-mini="leftPanelMini">
          <SearchSelector :disabled="disabledSearch"></SearchSelector>

          <PulseAndRiskSelector
            :disabled="disabledPulseAndRisk"
          ></PulseAndRiskSelector>

          <HotspotsSelector :disabled="disabledHotspots"></HotspotsSelector>

          <IncidentsSelector
            v-if="isIncidentsFeatureUnlocked && !hasEssentials"
          ></IncidentsSelector>

          <TravelTrackerSelector
            v-if="isIsosTravelTrackerFeatureUnlocked && !hasEssentials"
          ></TravelTrackerSelector>

          <InfluencesSelector
            v-if="isInfluencesFeatureUnlocked && !hasEssentials"
            :disabled="disabledInfluences"
          ></InfluencesSelector>

          <PowerOutageSelector
            v-if="isPowerOutageFeatureUnlocked && !hasEssentials"
          ></PowerOutageSelector>

          <CyberSelector v-if="hasPlusCyberAccess"></CyberSelector>
        </LeftSidePanelSection>

        <div class="hyp-left-side-panel-spacer"></div>

        <ReportingSelector
          v-if="isReportingFeatureUnlocked"
        ></ReportingSelector>
        <ProfileSelector></ProfileSelector>

        <SimpleSelector
          type="signout"
          title="Sign out"
          class="mt-4"
        ></SimpleSelector>
      </v-navigation-drawer>
    </div>
    <div
      class="hyp-logo-single-container"
      v-if="!isSelectorPanelVisible && !hasWatchOnly && !hasSearchOnly"
      @click="toggleSelectorPanel"
      title="Open menu"
    >
      <v-icon color="white" class="hyp-logo-single-carat pull-left"
        >fas fa-angle-left</v-icon
      >
      <Logo class-name="pull-left" type="white-bg" :collapsed="true"></Logo>
      <div class="clearfix"></div>
    </div>
  </div>
</template>

<script>
import lodashDebounce from "lodash/debounce";
import { mapGetters } from "vuex";
import { LeftSidePanelWidth } from "@/common/constants/layout";

import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_USER,
} from "@/store";
import { GET_SELECTOR_PANEL, SAVE_PREFERENCES } from "@/store/actions.type";
import {
  hasWatchPlusAccess,
  hasWatchOnlyAccess,
  hasSearchOnlyAccess,
  hasMassCommAdminAccess,
  hasEssentialsAccess,
  hasPlusCyberAccess,
  hasFiriAppAccess,
} from "@/services/user/user.service";
import { pageTrackerLogger } from "@/common/pageTracker";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  MINIMIZED: "minimized",
};

export default {
  name: "LeftSidePanel",
  components: {
    Logo: () => import("@/components/common/Logo"),
    LeftSidePanelSection: () =>
      import("@/components/common/LeftSidePanelSection"),
    SimpleSelector: () => import("@/components/common/SimpleSelector"),
    PlacesSelector: () => import("@/components/places/PlacesSelector"),
    PulseAndRiskSelector: () =>
      import("@/components/grids/PulseAndRiskSelector"),
    HotspotsSelector: () =>
      import("@/components/grids/hotspots/HotspotsSelector"),
    AlertsSelector: () => import("@/components/alerts/AlertsSelector"),
    NotificationsSelector: () =>
      import("@/components/notifications/NotificationsSelector"),
    InfluencesSelector: () =>
      import("@/components/influences/InfluencesSelector"),
    CollaborationSelector: () =>
      import("@/components/collaboration/CollaborationSelector"),
    ProfileSelector: () => import("@/components/profile/ProfileSelector"),
    MonitorSelector: () => import("@/components/monitor/MonitorSelector"),
    SearchSelector: () => import("@/components/search/SearchSelector"),
    CommsSelector: () => import("@/components/communication/CommsSelector"),
    IncidentsSelector: () =>
      import("@/components/controlRisks/incidents/IncidentsSelector"),
    TravelTrackerSelector: () =>
      import("@/components/isosTravelTracker/TravelTrackerSelector"),
    PowerOutageSelector: () =>
      import("@/components/powerOutage/PowerOutageSelector"),
    CyberSelector: () => import("@/components/cyber/CyberSelector"),
    LatestSelector: () => import("@/components/latest/LatestSelector"),
    ReportingSelector: () => import("@/components/reporting/ReportingSelector"),
  },
  mounted() {
    //TODO: This is overkill but we want this...
    this.handlePageTracker();
  },
  provide() {
    const leftPanel = {};
    Object.defineProperty(leftPanel, "mini", {
      enumerable: true,
      get: () => this.leftPanelMini,
    });
    return { leftPanel };
  },
  props: {
    selected: {
      type: String,
      default: "",
    },
  },
  data: function () {
    return {
      hasWebGL: this.isAvailableWebGL(),
      hasMassCommAdminAccess: hasMassCommAdminAccess(),
      hasWatchPlus: hasWatchPlusAccess(),
      hasWatchOnly: hasWatchOnlyAccess(),
      hasSearchOnly: hasSearchOnlyAccess(),
      hasEssentials: hasEssentialsAccess(),
      hasPlusCyberAccess: hasPlusCyberAccess(),
      hasFiriAppAccess: hasFiriAppAccess(),
      panelVisible: true,
      leftPanelMini: this.isLeftPanelMiniFeatureUnlocked,
      expandOnHover: this.isLeftPanelMiniFeatureUnlocked,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      places: MODULE_PREFIX_PLACES + "places",
    }),
    disabledSearch() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledComms() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledPlaces() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledPulseAndRisk() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledHotspots() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledEvents() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledInfluences() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledMedia() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledResources() {
      return !this.hasWebGL || this.hasWatchPlus;
    },
    disabledCollaboration() {
      return !this.hasWebGL;
    },
    disabledLatest() {
      return !this.hasWebGL || this.hasWatchPlus || this.hasWatchOnly;
    },
    disabledWatch() {
      return this.hasEssentials;
    },
    disabledAlerts() {
      return this.hasWatchOnly;
    },
    disabledNotifications() {
      return this.hasWatchOnly;
    },
    disableMenuToggle() {
      return this.hasWatchOnly;
    },
    isSelectorPanelVisible: {
      get() {
        return this.panelVisible;
      },
      set(value) {
        this.setMapZoomControls(value);
        this.panelVisible = value;
      },
    },
    logoType() {
      return "black-on-white-144";
    },
    isReportingFeatureUnlocked() {
      return this.featureUnlocked("reporting");
    },
    isLeftPanelMiniFeatureUnlocked() {
      return this.featureUnlocked("left-panel-mini");
    },
    isInfluencesFeatureUnlocked() {
      return this.featureUnlocked("influences");
    },
    isIncidentsFeatureUnlocked() {
      return (
        this.featureUnlocked("incidents") &&
        !this.featureUnlocked("livesearch-filter-events-v2")
      );
    },
    isIsosTravelTrackerFeatureUnlocked() {
      return this.featureUnlocked("isos-travel-tracker");
    },
    isPowerOutageFeatureUnlocked() {
      return this.featureUnlocked("power-outage-public");
    },
    isLatestFeatureUnlocked() {
      return this.featureUnlocked("latest-dashboard");
    },
    isFiriAppLinkFeatureUnlocked() {
      return this.featureUnlocked("firi-app-link");
    },
    isNotificationsFeatureUnlocked() {
      return this.featureUnlocked("notifications");
    },
    LeftSidePanelWidth() {
      return LeftSidePanelWidth;
    },
    navDrawerStyle() {
      return {
        backgroundImage: `url(${require("@/assets/images/seerist_sidebar_bg.png")})`,
      };
    },
  },
  methods: {
    handleUpdateMiniVariant(e) {
      this.leftPanelMini = e.srcElement.className.includes(
        "v-navigation-drawer--mini-variant"
      );
      this.$emit(EMITTED_ACTIONS.MINIMIZED, this.leftPanelMini);
    },
    toggleSelectorPanel: lodashDebounce(function () {
      this.isSelectorPanelVisible = !this.isSelectorPanelVisible;
      this.selectorPanel.visible = this.isSelectorPanelVisible;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        this.selectorPanel
      );

      let preferences = this.preferences || {};
      preferences.home = this.preferences.home || {};
      preferences.home.leftSidebar = this.preferences.home.leftSidebar || {};
      preferences.home.leftSidebar.visible = this.isSelectorPanelVisible;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    }, 800),
    setMapZoomControls(visible) {
      let mapZoomControls = document.querySelector(".mapboxgl-ctrl-top-left");
      if (mapZoomControls) {
        if (!visible) {
          mapZoomControls.style.top = "80px";
        } else {
          mapZoomControls.style.top = "0";
        }
      }
    },
    handlePageTracker() {
      pageTrackerLogger(
        "The left nav has loaded, Matomo will store track event system -> loggedIn."
      );
      const profile = JSON.parse(localStorage.getItem("user_profile"));
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.system,
        EVENT_ACTIONS.loggedIn,
        profile.email
      );
    },
  },
  watch: {
    "preferences.home.leftSidebar.visible": {
      handler() {
        this.isSelectorPanelVisible = this.preferences.home.leftSidebar.visible;
      },
    },
  },
};
</script>

<style>
.hyp-home-left-nav-dark {
  background-color: black !important;
  color: white !important;
}
.hyp-home-left-nav-light {
  background-color: white !important;
  background-image: "seerist_sidebar_bg.png";
  color: black !important;
}
.hyp-home-left-nav {
  float: left;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100vh;
}
.hyp-logo-single-container {
  cursor: pointer;
  width: 70px;
  height: 39px;
  position: absolute;
  top: 16px;
  left: 5px;
  z-index: 100;
}
.hyp-logo-single-carat {
  margin-right: 8px;
  position: relative;
  top: 7px;
}
.v-overlay {
  /* leftSidePanelWidth */
  left: 180px !important;
  background-color: rgba(0, 0, 0, 0.65) !important;
}
.hyp-left-side-panel-spacer {
  width: 100%;
  height: 50px;
}
.hyp-left-side-panel-spacer-small {
  width: 100%;
  height: 20px;
}
</style>
